// src/pages/battle/GetBattlePage.jsx

import { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useCourse as startCourse, getCourseDetail } from "../../api/courseApi";
import { getBattleResultList, startBattle } from "../../api/battleApi";

import BattlePageHeader from "../../components/battle/BattlePageHeader";
import BattleDetailInfoCard from "../../components/battle/BattleDetailInfoCard";
import BattleDetailActions from "../../components/battle/BattleDetailActions";
import BattleResultSection from "../../components/battle/BattleResultSection";

import CourseMapView from "../../components/course/CourseMapView";

import { useJoinBattle } from "../../hooks/useJoinBattle";
import { useFinishBattle } from "../../hooks/useFinishBattle";
import { useBattleDetail } from "../../hooks/useBattleDetail";

import { useJWT } from "../../contexts/JWTContext";
import { useToast } from "../../contexts/ToastContext";

// =====================
// util
// =====================
function useBattleIdFromQuery() {
  const location = useLocation();
  return useMemo(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get("battleId");
    return id ? Number(id) : null;
  }, [location.search]);
}

const modeLabel = (v) => (v === 0 ? "개인전" : v === 1 ? "팀전" : "-");
const statusLabel = (v) =>
  v === 0 ? "모집중" : v === 1 ? "모집완료" : v === 2 ? "종료" : "-";
const genderLabel = (v) =>
  v === 0 ? "혼성" : v === 1 ? "여자만" : v === 2 ? "남자만" : "-";

const formatDateTime = (dt) =>
  dt ? dt.replace("T", " ").slice(0, 16) : "-";

const isGenderAllowed = (battleGender, userGender) => {
  if (battleGender === 0) return true;
  if (userGender == null) return true;
  return battleGender === userGender;
};

// =====================
// component
// =====================
export default function GetBattlePage({ battleId: battleIdProp, onBack }) {
  const navigate = useNavigate();
  const battleIdFromQuery = useBattleIdFromQuery();
  const battleId = battleIdProp ?? battleIdFromQuery;

  const { userId: loginUserId, genderType: loginGenderType } = useJWT() ?? {};
  const { showToast } = useToast();

  const { battle, joinCount, loading, reload } = useBattleDetail(battleId);
  const { joining, joinBattle } = useJoinBattle();
  const { finishing, finishBattle } = useFinishBattle();

  const [showResult, setShowResult] = useState(false);
  const [resultList, setResultList] = useState([]);
  const [resultLoading, setResultLoading] = useState(false);
  const [resultError, setResultError] = useState(null);

  const [courseForMap, setCourseForMap] = useState(null);

  // =====================
  // course for map
  // =====================
  useEffect(() => {
    if (!battle?.courseId) return;

    let cancelled = false;

    (async () => {
      try {
        const data = await getCourseDetail(battle.courseId);
        if (!cancelled) {
          setCourseForMap(data.course || data);
        }
      } catch (e) {
        console.error(e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [battle?.courseId]);

  if (!battleId) return <div className="p-4">battleId 없음</div>;
  if (loading) return <div className="p-4">불러오는 중...</div>;
  if (!battle) return <div className="p-4">대결 없음</div>;

  // =====================
  // battle info
  // =====================
  const {
    battleName,
    courseId,
    userId: ownerId,
    mode,
    status,
    startDatetime,
    endDatetime,
    teamSize,
    genderType,
    alreadyJoined,
  } = battle;

  const modeNum = Number(mode);
  const statusNum = Number(status);
  const genderNum = Number(genderType);
  const teamSizeNum = teamSize != null ? Number(teamSize) : null;

// ✅ battleId/loginUserId가 있어야 키가 안정적이라서 여기서 계산
// 1순위: 서버에서 내려준 battle.workRecordId
// 2순위: 로컬스토리지(내가 startCourse로 만들었을 때 저장된 값)
const workRecordId =
  Number(battle?.workRecordId) ||
  Number(localStorage.getItem(`workRecordId:${battleId}:${loginUserId}`));




  const isCreator = loginUserId === ownerId;
  const safeAlreadyJoined = isCreator || !!alreadyJoined;

  const requiredCount =
    modeNum === 0 ? 2 : teamSizeNum ? teamSizeNum * 2 : 0;

  const isCapacityLeft =
    requiredCount > 0 ? joinCount < requiredCount : true;

  const isGenderOk = isGenderAllowed(genderNum, loginGenderType);

  const canJoin =
    statusNum === 0 &&
    loginUserId &&
    !isCreator &&
    !safeAlreadyJoined &&
    isGenderOk &&
    isCapacityLeft;

 const canStart =
   loginUserId &&
   safeAlreadyJoined &&
   (statusNum === 0 || statusNum === 1) &&
   joinCount >= requiredCount;

   console.log("START_BTN_DEBUG", {
  canStart,
  safeAlreadyJoined,
  alreadyJoined,
  isCreator,
  loginUserId,
  ownerId,
  statusNum,
  joinCount,
  requiredCount,
});

  // 🔥 자기대결 여부
const isSelfBattle = modeNum === 0 && isCreator;


  // 🔥 결과보기는 항상 노출
  const canViewResult = true;

  // =====================
  // handlers
  // =====================
  const handleJoin = async () => {
    try {
      await joinBattle({
        battleId,
        userId: loginUserId,
        team: 0,
      });
      showToast("대결에 참가했습니다.", "i");
      reload();
    } catch (e) {
      showToast("참가할 수 없습니다.", "e");
    }
  };

  const handleStart = async () => {
    // 🔥 자기대결 시작 방지
    if (isSelfBattle) {
      showToast("자기대결은 시작할 수 없습니다.", "w");
      return;
    }

    try {
      await startBattle(battleId);

      const resp = await startCourse(courseId, loginUserId, battleId);

      localStorage.setItem(`workRecordId:${battleId}:${loginUserId}`, String(resp.workRecordId));


      navigate("/course/using", {
        state: {
          course: resp.course,
          workRecordId: resp.workRecordId,
          userId: loginUserId,
        },
      });
    } catch (e) {
      showToast("대결을 시작할 수 없습니다.", "w");
    }
  };
const handleViewResult = async () => {
    console.log("workRecordId check:", {
    fromBattle: battle?.workRecordId,
    fromLocal: localStorage.getItem(`workRecordId:${battleId}:${loginUserId}`),
    final: workRecordId,
  });
  if (statusNum === 0) {
    showToast("진행 전 대결입니다.", "w");
    return;
  }

  setResultLoading(true);
  setResultError(null);

  try {
    if (!workRecordId) {
      showToast("운동 기록이 아직 없습니다. 코스를 시작/종료 후 결과를 확인하세요.", "w");
      return;
    }

    await finishBattle(battleId, workRecordId);
    await reload();

    const res = await getBattleResultList(battleId);
    const list = res?.data ?? res ?? [];
    setResultList(Array.isArray(list) ? list : []);
    setShowResult(true);
  } catch (e) {
    setResultError("결과 조회 실패");
    showToast("결과를 불러오지 못했습니다.", "e");
  } finally {
    setResultLoading(false);
  }
};


  return (
    <div className="min-h-screen flex justify-center bg-slate-100">
      <div className="w-full max-w-md flex flex-col">
        <BattlePageHeader title="대결 상세" showBack />

        <main className="flex-1 px-3 pt-2 pb-24 space-y-3">
          {courseForMap && (
            <CourseMapView
              pathData={courseForMap.coursePathList}
              startLocation={courseForMap.startPoint}
            />
          )}

          <BattleDetailInfoCard
            battleId={battleId}
            battleName={battleName}
            courseName={battle.courseName}
            ownerName={battle.ownerNickname}
            modeLabel={modeLabel(modeNum)}
            statusLabel={statusLabel(statusNum)}
            genderLabel={genderLabel(genderNum)}
            startDatetimeText={formatDateTime(startDatetime)}
            endDatetimeText={formatDateTime(endDatetime)}
        joinCountText={
  modeNum === 1 && teamSizeNum
    ? `${joinCount} / ${teamSizeNum * 2}`
    : `${joinCount} / 2`
}

            myStatusLabel={
              statusNum === 2
                ? safeAlreadyJoined
                  ? "종료 (참가)"
                  : "종료 (미참가)"
                : safeAlreadyJoined
                ? "참가중"
                : "미참가"
            }
          />

          <BattleDetailActions
            canJoin={canJoin}
            canStart={canStart}
            canViewResult={canViewResult}
            joining={joining}
            finishing={finishing}
            onJoin={handleJoin}
            onStart={handleStart}
            onViewResult={handleViewResult}
            showStartButton={true}
          />

          {showResult && (
            <BattleResultSection
              resultList={resultList}
              loading={resultLoading}
              error={resultError}
              loginUserId={loginUserId}
              onBackToDetail={() => setShowResult(false)}
            />
          )}
        </main>
      </div>
    </div>
  );
}
